import esupplyvetApi from "@/api/esupplyvet";
import { useRegisterStore } from "@/stores/register";
import api from "./index";

function getHttpClient() {
  const registerStore = useRegisterStore();
  const userRole = registerStore.user?.role || "";
  return userRole === "client" ? esupplyvetApi : api;
}


/**
 * Fetches all clients for promotions from the veterinary endpoint
 * @param {number} [page=1] - The page number to fetch
 * @param {number} [itemsPerPage=30] - Number of clients per page
 * @param {Object} [params={}] - Additional query parameters
 * @returns {Promise<{ clients: Array, totalItems: number }>} API response containing the list of clients
 */
export async function getPromotionClients(page = 1, itemsPerPage = 30, params = {}) {
  try {
    const httpClient = getHttpClient();
    
    const response = await httpClient.get("/promotions/client-veto", {
      headers: { Accept: "application/ld+json" },
      params: { page, itemsPerPage, ...params },
    });

    // Handle both Hydra format and regular array responses
    let clients = [];
    let totalItems = 0;

    if (response.data && response.data["hydra:member"]) {
      // Hydra format response
      clients = Array.isArray(response.data["hydra:member"]) ? response.data["hydra:member"] : [];
      totalItems = response.data["hydra:totalItems"] || 0;
    } else if (Array.isArray(response.data)) {
      // Regular array response
      clients = response.data;
      totalItems = response.data.length;
    } else if (response.data && Array.isArray(response.data.clients)) {
      // Object with clients array
      clients = response.data.clients;
      totalItems = response.data.totalItems || response.data.clients.length;
    }

    return {
      clients,
      totalItems,
      totalPages: Math.ceil(totalItems / itemsPerPage)
    };
  } catch (error) {
    console.error("Error fetching promotion clients:", error);
    throw error;
  }
}

/**
 * Fetches all clients with search and filter capabilities
 * @param {Object} options - Search and filter options
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.itemsPerPage=30] - Items per page
 * @param {string} [options.search=null] - Search term for client name or email
 * @param {string} [options.clientType=null] - Filter by client type
 * @param {boolean} [options.activeOnly=true] - Only return active clients
 * @returns {Promise<{ clients: Array, totalItems: number, totalPages: number }>}
 */
export async function getClients({
  page = 1,
  itemsPerPage = 30,
  search = null,
  clientType = null,
} = {}) {
  try {
    const params = { page, itemsPerPage };
    
    if (search) params.search = search;
    if (clientType) params.clientType = clientType;

    return await getPromotionClients(page, itemsPerPage, params);
  } catch (error) {
    console.error("Error fetching clients with filters:", error);
    throw error;
  }
}


/**
 * Creates a new promotion for clients
 * @param {Object} promotionData - Promotion data
 * @param {string} promotionData.name - Promotion name
 * @param {Array} promotionData.clients - Array of client IDs
 * @param {Object} promotionData.rules - Promotion rules
 * @param {Object} promotionData.actions - Promotion actions
 * @returns {Promise<Object>} Created promotion
 */
export async function createPromotion(promotionData) {
  try {
    const httpClient = getHttpClient();
    const response = await httpClient.post("/promotions/veterinaire", promotionData);
    return response.data;
  } catch (error) {
    console.error("Error creating promotion:", error);
    throw error;
  }
}

/**
 * Updates an existing promotion
 * @param {string|number} promotionId - Promotion ID
 * @param {Object} promotionData - Updated promotion data
 * @returns {Promise<Object>} Updated promotion
 */
export async function updatePromotion(promotionId, promotionData) {
  try {
    const httpClient = getHttpClient();
    const response = await httpClient.put(`/promotions/veterinaire/${promotionId}`, promotionData);
    return response.data;
  } catch (error) {
    console.error(`Error updating promotion ${promotionId}:`, error);
    throw error;
  }
}

/**
 * Deletes a promotion
 * @param {string|number} promotionId - Promotion ID
 * @returns {Promise<Object>} Delete response
 */
export async function deletePromotion(promotionId) {
  try {
    const httpClient = getHttpClient();
    const response = await httpClient.delete(`/promotions/${promotionId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting promotion ${promotionId}:`, error);
    throw error;
  }
}

/**
 * Fetches all promotions with pagination
 * @param {number} [page=1] - Page number
 * @param {number} [itemsPerPage=30] - Items per page
 * @returns {Promise<{ promotions: Array, totalItems: number }>}
 */
export async function getVeterinairePromotions(page = 1, itemsPerPage = 30) {
  try {
    const httpClient = getHttpClient();
    const response = await httpClient.get("/promotions/veterinaire", {
      headers: { Accept: "application/ld+json" },
      params: { page, itemsPerPage },
    });

    return {
      promotions: Array.isArray(response.data["hydra:member"]) ? response.data["hydra:member"] : [],
      totalItems: response.data["hydra:totalItems"] || 0,
      view: response.data["hydra:view"] || {},
      search: response.data["hydra:search"] || {}
    };
  } catch (error) {
    console.error("Error fetching veterinaire promotions:", error);
    throw error;
  }
}

/**
 * Fetches client promotions (for clients-proprietaires)
 * @param {number} [page=1] - Page number
 * @param {number} [itemsPerPage=30] - Items per page
 * @returns {Promise<{ promotions: Array, totalItems: number }>}
 */
export async function getClientPromotions(page = 1, itemsPerPage = 30) {
  try {
    const httpClient = getHttpClient();
    const response = await httpClient.get("/promotions/clients-propritaires/mes-promotions", {
      headers: { Accept: "application/ld+json" },
      params: { page, itemsPerPage },
    });

    return {
      promotions: Array.isArray(response.data["hydra:member"]) ? response.data["hydra:member"] : [],
      totalItems: response.data["hydra:totalItems"] || 0,
    };
  } catch (error) {
    console.error("Error fetching client promotions:", error);
    throw error;
  }
}

/**
 * Fetches client promotions with cumul information (for clients-proprietaires)
 * @param {number} [page=1] - Page number
 * @param {number} [itemsPerPage=30] - Items per page
 * @returns {Promise<{ promotions: Array, totalItems: number }>}
 */
export async function getClientPromotionsCumul(page = 1, itemsPerPage = 30) {
  try {
    const httpClient = getHttpClient();
    const response = await httpClient.get("/promotions/clients-propritaires/mes-promotions-cumul", {
      headers: { Accept: "application/ld+json" },
      params: { page, itemsPerPage },
    });

    return {
      promotions: Array.isArray(response.data["hydra:member"]) ? response.data["hydra:member"] : [],
      totalItems: response.data["hydra:totalItems"] || 0,
    };
  } catch (error) {
    console.error("Error fetching client promotions with cumul:", error);
    throw error;
  }
}

/**
 * Toggles promotion active status
 * @param {string|number} promotionId - Promotion ID
 * @param {boolean} active - New active status
 * @returns {Promise<Object>} Updated promotion
 */
export async function togglePromotionStatus(promotionId, active) {
  try {
    const httpClient = getHttpClient();
    const response = await httpClient.put(`/promotions/veterinaire/${promotionId}`, {
      enabled: active
    });
    return response.data;
  } catch (error) {
    console.error(`Error toggling promotion status ${promotionId}:`, error);
    throw error;
  }
}

export default {
  getPromotionClients,
  getClients,
  createPromotion,
  updatePromotion,
  deletePromotion,
  getVeterinairePromotions,
  getClientPromotions,
  getClientPromotionsCumul,
  togglePromotionStatus
};